// Public subscribe affordance for /series/:seriesid. Ported BEHAVIOUR (not code) from OCDevel's
// `settings/PublishCard.tsx::PublicSubscribe` + `PublishModal.tsx::PLATFORM_META`: those imported
// the store, `@shared/ui/modal` and `useCool`, none of which may appear in this route's import
// graph — it is eagerly loaded during Node prerender (see series.tsx). Everything here is
// loader-data in, MUI out; `navigator.clipboard` is only ever reached from an onClick.
import { useState } from 'react';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import AppleIcon from '@mui/icons-material/Apple';
import PodcastsIcon from '@mui/icons-material/Podcasts';
import LinkIcon from '@mui/icons-material/Link';

/** Known platforms get a brand-ish icon + tidy label; anything else falls back to the raw string + a generic link icon (`platform` is free text — no enum, no uniqueness). */
const PLATFORM_META: Record<string, { label: string; icon: React.ReactElement }> = {
  apple: { label: 'Apple Podcasts', icon: <AppleIcon fontSize="small" /> },
  spotify: { label: 'Spotify', icon: <PodcastsIcon fontSize="small" /> },
  youtube: { label: 'YouTube', icon: <PodcastsIcon fontSize="small" /> },
  overcast: { label: 'Overcast', icon: <PodcastsIcon fontSize="small" /> },
  pocketcasts: { label: 'Pocket Casts', icon: <PodcastsIcon fontSize="small" /> },
  amazon: { label: 'Amazon Music', icon: <PodcastsIcon fontSize="small" /> },
};

/** Copyable read-only URL. A naked <Link> to a feed dumps raw XML at the visitor — that is the loss this whole module exists to fix, so the URL is never an anchor. */
function CopyField({ label, value, helper }: { label: string; value: string; helper?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }, () => {});
  };
  return (
    <TextField
      label={label} value={value} helperText={helper} size="small" fullWidth
      slotProps={{
        htmlInput: { readOnly: true, onFocus: (e: React.FocusEvent<HTMLInputElement>) => e.target.select() },
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title={copied ? 'Copied' : 'Copy'}>
                <IconButton onClick={copy} size="small" aria-label={`Copy ${label}`}>
                  {copied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
        },
      }}
    />
  );
}

export default function SubscribePanel({ feedUrl, links }: {
  feedUrl: string; links: Array<{ platform: string; url: string }>;
}) {
  const [open, setOpen] = useState(false);
  const textFeedUrl = feedUrl.replace(/\.xml$/, '.text.xml');

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <Button variant="contained" startIcon={<RssFeedIcon />} onClick={() => setOpen((o) => !o)}
          aria-expanded={open} sx={{ alignSelf: 'flex-start' }}>
          Subscribe
        </Button>
        {/* index key, not `platform`: it is free text with no uniqueness constraint, so two "spotify" rows collide into one React key.
            Scheme-gated HERE as well as at the artifact write (directory.ts `publicShow`), the same belt-and-braces `rss-feed.ts::safeHref` applies to `website`: this page is PRERENDERED from whatever artifact existed at build time, so a build that precedes the next regen still bakes pre-refine rows — and React renders a `javascript:` href verbatim. */}
        {links.filter((l) => /^https?:\/\//i.test(l?.url ?? '')).map((l, i) => {
          const meta = PLATFORM_META[l.platform.trim().toLowerCase()];
          return (
            <Chip key={i} component="a" href={l.url} target="_blank" rel="noreferrer" clickable
              variant="outlined" icon={meta?.icon ?? <LinkIcon fontSize="small" />}
              label={meta?.label ?? l.platform} />
          );
        })}
      </Stack>
      <Collapse in={open} unmountOnExit>
        <Stack spacing={2} sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
          <CopyField label="RSS feed" value={feedUrl} helper="Paste this into any podcast app." />
          <CopyField label="Text feed (for e-readers)" value={textFeedUrl}
            helper="Same episodes as shownotes text, no audio." />
          <Typography variant="body2" color="text.secondary">
            Need the Apple Podcasts or AntennaPod steps?{' '}
            <Link href="/publishing#subscribe" target="_blank" rel="noopener noreferrer">Open the publishing guide ↗</Link>
          </Typography>
        </Stack>
      </Collapse>
    </Stack>
  );
}
